import { Controller } from '@hotwired/stimulus'
import 'bootstrap-datepicker'
import {
  trans,
  DOB_INCOMPLETE_ERROR,
  DOB_INVALID_DATE_ERROR,
  DOB_APPARENT_AGE_RANGE_TOO_WIDE_ERROR,
  DOB_APPARENT_AGE_OUT_OF_BOUNDS_ERROR,
  DOB_APPARENT_AGE_LINK,
  DOB_APPARENT_AGE_HIDE_LINK,
  YEAR_RANGE
} from '../../translator'

declare const accountDateFormat: string

// A give-or-take above this would make a span (2 x give-or-take) wider than
// PartialDateOfBirth's 20-year cap (MAX_YEAR_SPAN), so it is refused here rather
// than left for the server to reject.
const MAX_GIVE_OR_TAKE = 10

// Mirrors PartialDateOfBirth::MIN_YEAR/MAX_YEAR
const MIN_YEAR = 1000
const MAX_YEAR = 9999

/**
 * Drives the three-box date-of-birth entry (day, month, year) and the apparent-age
 * panel that derives a year range from "looked about X, give or take Y, seen on Z".
 *
 * Keeps two hidden inputs, dob_year_from and dob_year_to, in sync with the year box;
 * the day and month boxes carry dob_day and dob_month directly. associated-person-form
 * reads all four by name to build date_of_birth_parts, exactly as it already reads
 * every other field. This controller never talks to associated-person-form directly.
 *
 * A year range (from the apparent-age panel, or already held on an edit) cannot carry
 * a day or month, matching the value object's rule, so the day and month boxes are
 * cleared and disabled while a range is shown; a "clear" button restores plain entry.
 */
export default class DobEntryController extends Controller {
  static targets = [
    'day', 'month', 'year', 'dayColumn', 'monthColumn', 'yearColumn', 'approximate',
    'yearFromHidden', 'yearToHidden',
    'apparentAgeToggleLabel', 'apparentAgePanel', 'lookedAbout', 'giveOrTake', 'whenSeen', 'apparentAgeError',
    'clearRange', 'error', 'cannotBeScreened'
  ]

  static values = { yearFrom: String, yearTo: String, month: String, day: String }

  declare readonly dayTarget: HTMLInputElement
  declare readonly monthTarget: HTMLInputElement
  declare readonly yearTarget: HTMLInputElement
  declare readonly dayColumnTarget: HTMLElement
  declare readonly monthColumnTarget: HTMLElement
  declare readonly yearColumnTarget: HTMLElement
  declare readonly approximateTarget: HTMLInputElement
  declare readonly yearFromHiddenTarget: HTMLInputElement
  declare readonly yearToHiddenTarget: HTMLInputElement
  declare readonly apparentAgeToggleLabelTarget: HTMLElement
  declare readonly apparentAgePanelTarget: HTMLElement
  declare readonly lookedAboutTarget: HTMLInputElement
  declare readonly giveOrTakeTarget: HTMLInputElement
  declare readonly whenSeenTarget: HTMLInputElement
  declare readonly apparentAgeErrorTarget: HTMLElement
  declare readonly clearRangeTarget: HTMLElement
  declare readonly errorTarget: HTMLElement
  declare readonly cannotBeScreenedTarget: HTMLElement

  declare readonly yearFromValue: string
  declare readonly yearToValue: string
  declare readonly monthValue: string
  declare readonly dayValue: string

  private hasRange = false
  private rangeFrom: number | null = null
  private rangeTo: number | null = null

  connect (): void {
    const yearFrom = this.numberOrNull(this.yearFromValue)
    const yearTo = this.numberOrNull(this.yearToValue)
    const month = this.numberOrNull(this.monthValue)
    const day = this.numberOrNull(this.dayValue)
    const isFullDate = yearFrom !== null && yearFrom === yearTo && month !== null && day !== null

    if (day !== null) {
      this.dayTarget.value = String(day)
    }
    if (month !== null) {
      this.monthTarget.value = String(month)
    }

    if (yearFrom !== null && yearTo !== null && yearFrom !== yearTo) {
      this.enterRangeMode(yearFrom, yearTo)
    } else if (yearFrom !== null) {
      this.yearTarget.value = String(yearFrom)
    }

    this.approximateTarget.checked = !isFullDate && (yearFrom !== null || month !== null || day !== null)
    this.syncHidden()
    this.updateCannotBeScreenedNotice()
    this.initializeWhenSeenDatepicker()
  }

  /**
   * Wired ahead of onFieldInput on day, month, year, looked-about and give-or-take: strips
   * anything but digits as the user types, since inputmode="numeric" only hints at a numeric
   * keyboard on mobile and does nothing to stop a physical keyboard from typing letters.
   */
  restrictToDigits (event: Event): void {
    const target = event.target as HTMLInputElement
    const digitsOnly = target.value.replace(/\D/g, '')

    if (digitsOnly !== target.value) {
      target.value = digitsOnly
    }
  }

  onFieldInput (): void {
    this.hideError()
    this.syncHidden()
    this.updateCannotBeScreenedNotice()
    this.syncApproximateWithCompleteness()
  }

  /** Validation runs on blur, not on every keystroke, so the error doesn't flash while a box is still being typed into. */
  onFieldBlur (): void {
    this.validate()
  }

  toggleApparentAgePanel (): void {
    const isOpen = !this.apparentAgePanelTarget.classList.contains('d-none')

    if (isOpen) {
      this.closeApparentAgePanel()
    } else {
      this.apparentAgePanelTarget.classList.remove('d-none')
      this.apparentAgeToggleLabelTarget.textContent = trans(DOB_APPARENT_AGE_HIDE_LINK, {}, 'associated-persons')

      // Defaults "when seen" to today, the common case, without clobbering an earlier
      // edit if the user closes and reopens the panel.
      if (this.whenSeenDate() === null) {
        this.setWhenSeenDate(new Date())
      }
    }
  }

  private closeApparentAgePanel (): void {
    this.apparentAgePanelTarget.classList.add('d-none')
    this.apparentAgeToggleLabelTarget.textContent = trans(DOB_APPARENT_AGE_LINK, {}, 'associated-persons')
  }

  /** Binds bootstrap-datepicker to whenSeenTarget, rendering and parsing it in the account's configured date format rather than the browser's own locale. */
  private initializeWhenSeenDatepicker (): void {
    const jquery = (window as unknown as {
      $?: (selector: HTMLElement) => { datepicker: (options: { autoclose: boolean, format: string, zIndexOffset: string }) => unknown }
    }).$

    jquery?.(this.whenSeenTarget).datepicker({ autoclose: true, format: accountDateFormat, zIndexOffset: '2000' })
  }

  /** Reads the date held by the picker via its own parsed state, rather than re-parsing the displayed (account-format-dependent) string. Null if nothing has been chosen, or jQuery/the plugin never loaded. */
  private whenSeenDate (): Date | null {
    const jquery = (window as unknown as {
      $?: (selector: HTMLElement) => { data: (key: string) => { getDate: () => Date | null } | undefined }
    }).$

    return jquery?.(this.whenSeenTarget).data('datepicker')?.getDate() ?? null
  }

  private setWhenSeenDate (date: Date): void {
    const jquery = (window as unknown as {
      $?: (selector: HTMLElement) => { datepicker: (action: string, date: Date) => unknown }
    }).$

    jquery?.(this.whenSeenTarget).datepicker('setDate', date)
  }

  /** "Use it": derives the year range from the apparent age and closes the panel. */
  useApparentAge (): void {
    this.hideApparentAgeError()

    const lookedAbout = this.numberOrNull(this.lookedAboutTarget.value)
    const whenSeen = this.whenSeenDate()

    if (lookedAbout === null || whenSeen === null) {
      return
    }

    const giveOrTake = this.numberOrNull(this.giveOrTakeTarget.value) ?? 0

    if (giveOrTake > MAX_GIVE_OR_TAKE) {
      this.showApparentAgeError(trans(DOB_APPARENT_AGE_RANGE_TOO_WIDE_ERROR, {}, 'associated-persons'))

      return
    }

    const seenYear = whenSeen.getFullYear()
    const rangeFrom = seenYear - lookedAbout - giveOrTake
    const rangeTo = seenYear - lookedAbout + giveOrTake

    if (rangeFrom < MIN_YEAR || rangeFrom > MAX_YEAR || rangeTo < MIN_YEAR || rangeTo > MAX_YEAR) {
      this.showApparentAgeError(trans(DOB_APPARENT_AGE_OUT_OF_BOUNDS_ERROR, { '%min%': MIN_YEAR, '%max%': MAX_YEAR }, 'associated-persons'))

      return
    }

    this.enterRangeMode(rangeFrom, rangeTo)
    this.approximateTarget.checked = true
    this.hideError()
    this.syncHidden()
    this.updateCannotBeScreenedNotice()
    this.closeApparentAgePanel()
  }

  /** Abandons the apparent-age estimate and returns to plain day/month/year entry. */
  clearRange (): void {
    this.exitRangeMode()
    this.yearTarget.value = ''
    this.syncHidden()
    this.updateCannotBeScreenedNotice()
  }

  /** A year range is definitionally approximate, so unticking the box while one is held clears it, exactly as the clear button does. */
  onApproximateChange (): void {
    if (!this.approximateTarget.checked && this.hasRange) {
      this.clearRange()
    }
  }

  /**
   * Checks the boxes against the value object's rules, so the same shape of error the
   * server would give on save (dob_incomplete_error, dob_invalid_date_error) is shown
   * inline as the user leaves the fields, without waiting on a round trip. Returns
   * whether the current entry is valid.
   */
  validate (): boolean {
    this.hideError()

    if (this.hasRange) {
      return true
    }

    const day = this.numberOrNull(this.dayTarget.value)
    const month = this.numberOrNull(this.monthTarget.value)
    const year = this.numberOrNull(this.yearTarget.value)
    const filledCount = [day, month, year].filter(value => value !== null).length

    if (year !== null && (year < MIN_YEAR || year > MAX_YEAR)) {
      this.showError(trans(DOB_INVALID_DATE_ERROR, {}, 'associated-persons'))

      return false
    }

    // Checked before the ticked/unticked branch below: completing all three boxes unticks
    // approximate (syncApproximateWithCompleteness), so a full date's calendar validity must
    // not depend on that checkbox's state, or it would never actually run.
    if (day !== null && month !== null && !this.isValidCalendarDate(year, month, day)) {
      this.showError(trans(DOB_INVALID_DATE_ERROR, {}, 'associated-persons'))

      return false
    }

    if (day !== null && month !== null && year !== null && this.isFutureDate(year, month, day)) {
      this.showError(trans(DOB_INVALID_DATE_ERROR, {}, 'associated-persons'))

      return false
    }

    if (!this.approximateTarget.checked) {
      if (filledCount > 0 && filledCount < 3) {
        this.showError(trans(DOB_INCOMPLETE_ERROR, {}, 'associated-persons'))

        return false
      }

      return true
    }

    if (day !== null && month === null) {
      this.showError(trans(DOB_INVALID_DATE_ERROR, {}, 'associated-persons'))

      return false
    }

    if (month !== null && day === null && year === null) {
      this.showError(trans(DOB_INVALID_DATE_ERROR, {}, 'associated-persons'))

      return false
    }

    return true
  }

  private isValidCalendarDate (year: number | null, month: number, day: number): boolean {
    const date = new Date(year ?? 2000, month - 1, day)

    return date.getMonth() === month - 1 && date.getDate() === day
  }

  private isFutureDate (year: number, month: number, day: number): boolean {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    return new Date(year, month - 1, day) > today
  }

  /** Hides the now-irrelevant day/month boxes and gives the year box their room: "1991 to 2001" needs far more width than "YYYY" ever did. */
  private enterRangeMode (from: number, to: number): void {
    this.hasRange = true
    this.rangeFrom = from
    this.rangeTo = to
    this.dayTarget.value = ''
    this.monthTarget.value = ''
    this.dayTarget.disabled = true
    this.monthTarget.disabled = true
    this.yearTarget.readOnly = true
    this.yearTarget.value = trans(YEAR_RANGE, { '%from%': from, '%to%': to }, 'associated-persons')
    this.clearRangeTarget.classList.remove('d-none')
    // d-none loses to the column's own d-flex on this element (Bootstrap's display
    // utilities share one !important tier, and "flex" is generated after "none"), so
    // d-flex has to come off before d-none can actually hide the column.
    this.dayColumnTarget.classList.remove('d-flex', 'flex-column', 'justify-content-end')
    this.dayColumnTarget.classList.add('d-none')
    this.monthColumnTarget.classList.remove('d-flex', 'flex-column', 'justify-content-end')
    this.monthColumnTarget.classList.add('d-none')
    // col-2's own max-width still caps it at one-sixth of the row even once flex-grow
    // is turned on, so that has to be lifted too or "1991 to 2001" clips just the same.
    this.yearColumnTarget.classList.add('flex-grow-1', 'mw-100')
  }

  private exitRangeMode (): void {
    this.hasRange = false
    this.rangeFrom = null
    this.rangeTo = null
    this.dayTarget.disabled = false
    this.monthTarget.disabled = false
    this.yearTarget.readOnly = false
    this.clearRangeTarget.classList.add('d-none')
    this.dayColumnTarget.classList.remove('d-none')
    this.dayColumnTarget.classList.add('d-flex', 'flex-column', 'justify-content-end')
    this.monthColumnTarget.classList.remove('d-none')
    this.monthColumnTarget.classList.add('d-flex', 'flex-column', 'justify-content-end')
    this.yearColumnTarget.classList.remove('flex-grow-1', 'mw-100')
  }

  private syncHidden (): void {
    if (this.hasRange) {
      this.yearFromHiddenTarget.value = this.rangeFrom !== null ? String(this.rangeFrom) : ''
      this.yearToHiddenTarget.value = this.rangeTo !== null ? String(this.rangeTo) : ''

      return
    }

    const year = this.numberOrNull(this.yearTarget.value)
    this.yearFromHiddenTarget.value = year !== null ? String(year) : ''
    this.yearToHiddenTarget.value = year !== null ? String(year) : ''
  }

  /**
   * Keeps "This is approximate" in step with the boxes: completing all three unticks it
   * (a full date isn't approximate), and emptying any one of them while unticked ticks it
   * back on (a part date needs the box ticked to save). A fully empty entry is left alone,
   * since "no date known" isn't a part date either. The user can still override the box by
   * hand; the next edit that changes completeness re-applies this rule regardless.
   */
  private syncApproximateWithCompleteness (): void {
    if (this.hasRange) {
      return
    }

    const day = this.numberOrNull(this.dayTarget.value)
    const month = this.numberOrNull(this.monthTarget.value)
    const year = this.numberOrNull(this.yearTarget.value)
    const filledCount = [day, month, year].filter(value => value !== null).length

    if (filledCount === 3) {
      this.approximateTarget.checked = false
    } else if (filledCount > 0) {
      this.approximateTarget.checked = true
    }
  }

  /** Shown only while nothing at all is held: an empty date of birth is not a partial one. */
  private updateCannotBeScreenedNotice (): void {
    const allEmpty = !this.hasRange &&
      this.dayTarget.value.trim() === '' &&
      this.monthTarget.value.trim() === '' &&
      this.yearTarget.value.trim() === ''
    this.cannotBeScreenedTarget.classList.toggle('d-none', !allEmpty)
  }

  private numberOrNull (value: string): number | null {
    const trimmed = value.trim()
    if (trimmed === '') {
      return null
    }

    const parsed = Number(trimmed)

    return Number.isInteger(parsed) ? parsed : null
  }

  private showError (message: string): void {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('d-none')
  }

  private hideError (): void {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('d-none')
  }

  private showApparentAgeError (message: string): void {
    this.apparentAgeErrorTarget.textContent = message
    this.apparentAgeErrorTarget.classList.remove('d-none')
  }

  private hideApparentAgeError (): void {
    this.apparentAgeErrorTarget.textContent = ''
    this.apparentAgeErrorTarget.classList.add('d-none')
  }
}
